import { useEffect, useRef } from 'react';
import { supabase, chatSupabase } from '@/integrations/supabase/client';
import { useOrganizationContext } from '@/contexts/OrganizationContext';

export function useQueueAutoDistribution() {
  const { currentOrg } = useOrganizationContext();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isProcessingRef = useRef(false);

  useEffect(() => {
    if (!currentOrg?.id) {
      return;
    }

    // Função para distribuir chats da fila
    const distributeQueueChats = async () => {
      // Evitar múltiplas execuções simultâneas
      if (isProcessingRef.current) {
        return;
      }

      try {
        isProcessingRef.current = true;

        // 1. Verificar se a distribuição automática está ativada
        const { data: queueSettings, error: settingsError } = await supabase
          .from('queue_settings')
          .select('*')
          .eq('org_id', currentOrg.id)
          .maybeSingle();

        if (settingsError) {
          console.error('Erro ao buscar configurações da fila:', settingsError);
          return;
        }

        if (!queueSettings || !queueSettings.auto_distribution_enabled) {
          // Distribuição automática desativada, não fazer nada
          return;
        }

        // 2. Buscar chats na fila humana (não atribuídos) — fonte: chats_Instagram
        const { data: queueChats, error: chatsError } = await chatSupabase
          .from('conversations')
          .select('chat_id, contact_id, org_id, status_atd, user_atribuido')
          .eq('org_id', currentOrg.id)
          .eq('status_atd', 'fila humana')
          .is('user_atribuido', null)
          .limit(10); // Processar até 10 chats por vez para não sobrecarregar

        if (chatsError) {
          console.error('Erro ao buscar chats da fila:', chatsError);
          return;
        }

        if (!queueChats || queueChats.length === 0) {
          // Nenhum chat na fila
          return;
        }

        // 3. Buscar operadores disponíveis
        const { data: members, error: membersError } = await supabase
          .from('organization_members')
          .select('user_id')
          .eq('org_id', currentOrg.id)
          .eq('role', 'member'); // Apenas members, não admins/owners

        if (membersError) {
          console.error('Erro ao buscar membros:', membersError);
          return;
        }

        if (!members || members.length === 0) {
          // Nenhum operador encontrado
          return;
        }

        // Buscar status dos operadores
        const userIds = members.map((m: any) => m.user_id).filter(Boolean);
        const { data: profiles, error: profilesError } = await supabase
          .from('profiles')
          .select('id, user_status')
          .in('id', userIds)
          .eq('user_status', 'disponivel');

        if (profilesError) {
          console.error('Erro ao buscar perfis:', profilesError);
          return;
        }

        const availableOperators = profiles?.map((p: any) => p.id) || [];

        if (availableOperators.length === 0) {
          console.log('⚠️ Nenhum operador disponível no momento');
          return;
        }

        console.log(`👥 ${availableOperators.length} operador(es) disponível(eis)`);

        // 4. Distribuir chats conforme a estratégia
        let selectedOperatorIndex = queueSettings.last_assigned_user_index || 0;

        for (const chat of queueChats) {
          let selectedOperator: string | null = null;

          if (queueSettings.distribution_strategy === 'round-robin') {
            // Round-robin: distribuir em ordem circular
            const nextIndex = selectedOperatorIndex % availableOperators.length;
            selectedOperator = availableOperators[nextIndex];
            selectedOperatorIndex = nextIndex + 1;

            // Atualizar índice no banco
            await supabase
              .from('queue_settings')
              .update({ last_assigned_user_index: selectedOperatorIndex })
              .eq('id', queueSettings.id);
          } else if (queueSettings.distribution_strategy === 'least-busy') {
            // Least-busy: contar chats atribuídos para cada operador
            const operatorCounts: { [key: string]: number } = {};

            for (const operatorId of availableOperators) {
              const { count, error: countError } = await chatSupabase
                .from('conversations')
                .select('chat_id', { count: 'exact', head: true })
                .eq('org_id', currentOrg.id)
                .eq('user_atribuido', operatorId)
                .not('status_atd', 'in', ['chat finalizado', 'Chat Encerrado']);

              if (!countError) {
                operatorCounts[operatorId] = count || 0;
              }
            }

            // Selecionar operador com menos chats
            selectedOperator = availableOperators.reduce((prev, current) => {
              return (operatorCounts[current] || 0) < (operatorCounts[prev] || 0) ? current : prev;
            });
          } else {
            // Manual: não distribuir automaticamente
            continue;
          }

          if (selectedOperator) {
            // Atribuir o chat ao operador (atualizar chats_Instagram)
            const { error: assignError } = await chatSupabase
              .from('conversations')
              .update({
                user_atribuido: selectedOperator,
              })
              .eq('chat_id', chat.chat_id);

            if (assignError) {
              console.error(`Erro ao atribuir chat ${chat.chat_id}:`, assignError);
            } else {
              console.log(`✅ Chat ${chat.chat_id} atribuído ao operador ${selectedOperator}`);
            }
          }
        }
      } catch (error) {
        console.error('Erro na distribuição automática da fila:', error);
      } finally {
        isProcessingRef.current = false;
      }
    };

    // Executar imediatamente na primeira vez
    distributeQueueChats();

    // Configurar intervalo para verificar a cada 30 segundos
    intervalRef.current = setInterval(() => {
      distributeQueueChats();
    }, 30000); // 30 segundos

    // Cleanup: limpar intervalo quando o componente desmontar ou org mudar
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [currentOrg?.id]);

  // Não retorna nada, apenas executa em background
  return null;
}
